import { authService } from './authService';
import { CORE_API_URL } from '../config';

// The tenant-facing client for their own ATS connection (Task 5's
// cmd/api/ats_connection.go), a NEW surface distinct from integrationService.js's
// path 2 (the API/worker connector, which has never run anywhere). Keeping the
// two separate is what lets path 2 later be completed or retired as a clean cut.
//
// The API never returns a stored password -- GET sends has_secret, a boolean
// derived from an EXISTS, never the ciphertext -- so nothing here can carry one
// either.

async function request(path, { method = 'GET', body } = {}) {
  const token = authService.requireToken();

  const init = {
    method,
    headers: { Authorization: `Bearer ${token}` },
  };
  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  let response;
  try {
    response = await fetch(`${CORE_API_URL}${path}`, init);
  } catch (e) {
    throw new Error(
      `Network error: unable to reach the ATS connection API at ${CORE_API_URL}. ` +
        'Check that the Go api service is running.'
    );
  }

  if (!response.ok) {
    let detail = `API returned status ${response.status}`;
    try {
      const errBody = await response.json();
      detail = errBody.detail || detail;
    } catch (e) {
      /* keep the status-code message */
    }
    const err = new Error(detail);
    // Callers distinguish "nothing configured yet" (404) from a real failure
    // without parsing the message text -- see AtsConnectionForm's initial load.
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export const atsConnectionService = {
  // Returns the stored connection, or throws (404 -- surfaced as an Error --
  // when nothing is configured yet; callers treat "not found" as the empty state).
  async get() {
    return request('/api/ats-connection');
  },

  // body: {endpoint, namespace, username, password?}. Omit password entirely to
  // leave the stored one byte-identical -- an empty string is a distinct,
  // meaningful value to the API, not "unchanged".
  async save(body) {
    return request('/api/ats-connection', { method: 'PUT', body });
  },

  // Dials the real database. Returns {success: true} or {success: false, reason}
  // on an ordinary failure (200); a 503 (no server cipher configured) instead
  // rejects through request()'s throw path, so callers must handle both.
  async test() {
    return request('/api/ats-connection/test', { method: 'POST', body: {} });
  },

  async setEnabled(enabled) {
    return request('/api/ats-connection/enable', { method: 'POST', body: { enabled } });
  },
};
